// shared/src/schemas/qa-answer-outcome.ts
// Zod guard + row type for `qa_answer_outcomes` (DDL: 0060_qa_calibration.sql).
// One row per scored Q&A answer (UNIQUE per scorer_version). Written by
// workers/macro-prediction-scorer runQaCalibrationPass; read by api-worker's
// qa-calibration aggregator. Field names mirror the DDL 1:1 (schema-vs-ddl gate).

import { z } from 'zod';

export const QaOutcomeEnum = z.enum(['correct', 'incorrect', 'neutral', 'partial']);
export const QaScoredViaEnum = z.enum(['deterministic', 'llm']);
export const QaWinningPerspectiveRowEnum = z.enum([
  'consensus', 'contrarian', 'trajectory', 'predicted', 'tie', 'unresolved',
]);

export const QaAnswerOutcomeSchema = z.object({
  id:                    z.string(),
  answer_id:             z.string(),
  question_id:           z.string(),
  brief_id:              z.string(),
  resolves_at:           z.string(),
  realized_value:        z.number().nullable(),
  realized_at:           z.string(),
  outcome:               QaOutcomeEnum,
  predicted_confidence:  z.enum(['HIGH', 'MED', 'LOW']),
  predicted_prob:        z.number(),
  brier_contribution:    z.number().nullable(),
  scored_via:            QaScoredViaEnum,
  input_categories_json: z.string(),
  winning_perspective:   QaWinningPerspectiveRowEnum,
  scorer_version:        z.string(),
  scored_at:             z.string(),
});

export type QaAnswerOutcomeRow = z.infer<typeof QaAnswerOutcomeSchema>;

// HIGH/MED/LOW → representative probability for reliability bins + Brier.
// Server-side ONLY (never fed to the LLM — preserves no-numeric-anchors). The
// three levels are deliberately spread around 0.5 (LOW=0.40 stays a directional
// commit, not a coin flip, since the model still picked a side).
export const CONFIDENCE_PROB: Readonly<Record<'HIGH' | 'MED' | 'LOW', number>> = {
  HIGH: 0.80,
  MED:  0.60,
  LOW:  0.40,
};

// The input categories an answer's citations can map to (D2a attribution).
export const QA_INPUT_CATEGORIES = ['price', 'events', 'macro', 'news', 'trajectory'] as const;
export type QaInputCategory = (typeof QA_INPUT_CATEGORIES)[number];
